import React, { type FC, useCallback, useEffect, useState } from 'react';

import { Plus } from '@gravity-ui/icons';
import { Alert, Icon } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { granetApi } from 'client/api';
import { AliceAppActionButton } from 'client/blocks/AliceAppActionButton/AliceAppActionButton';
import { GrammarPageIntent } from 'client/blocks/grammar/GrammarPageIntent/GrammarPageIntent';
import { PageLoader } from 'client/blocks/Loader/PageLoader';
import { GrammarPageContext } from 'client/context/grammarPageContext';
import { useSettingsPageContext } from 'client/context/settingsPageContext';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { GrammarIntentShort } from 'client/model/grammar';
import { Skill } from 'client/model/skill';
import * as keyset from 'i18n/IntentsPage';

import styles from './GrammarPageIntents.module.scss';

interface Props {
    aliceApp: Skill;
    disabled: boolean;
}

const t = i18n(keyset);

export const GrammarPageIntents: FC<Props> = ({ aliceApp, disabled }) => {
    const { isDraft, isEditable } = useSettingsPageContext();
    const [intents, setIntents] = useState<GrammarIntentShort[]>([]);

    const fetchIntents = useCallback(
        () =>
            granetApi.getGrammarIntentList({
                skillId: aliceApp.id,
                channel: aliceApp.channel,
                isDraft,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDraft, aliceApp.id],
    );

    const [fetchIntentsHandler, state] = useFetchingHandler({
        handler: () =>
            granetApi.getGrammarIntentList({
                skillId: aliceApp.id,
                channel: aliceApp.channel,
                isDraft,
            }),
        onLoad: setIntents,
    });

    const [createIntent] = useFetchingHandler({
        omitResult: true,
        throwErrors: true,
        handler: () => granetApi.createGrammarIntent({ skillId: aliceApp.id, channel: aliceApp.channel }),
        onLoad: (intent) => setIntents([...intents, intent]),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void fetchIntentsHandler(), [isDraft]);

    if (state === 'loading') {
        return <PageLoader />;
    }

    if (state === 'error') {
        return (
            <Alert
                className={styles.grammarErrorAlert__container}
                title={<span className={styles.grammarErrorAlert__title}>{t('error')}</span>}
                theme="danger"
                view="outlined"
            />
        );
    }

    return (
        <GrammarPageContext.Provider
            value={{
                refreshIntents: () => fetchIntents().then(setIntents),
                removeIntentFromView: (id) => setIntents(intents.filter((intent) => intent.id !== id)),
            }}
        >
            {intents.map((intent) => (
                <GrammarPageIntent key={intent.id} intent={intent} />
            ))}
            <AliceAppActionButton successLabel="" action={createIntent} disabled={disabled || !isEditable} size="l">
                <Icon data={Plus} />
                {t('create-intent')}
            </AliceAppActionButton>
        </GrammarPageContext.Provider>
    );
};
